import React, { useState } from 'react';
import { Group } from 'react-konva';

import { useLatest } from 'src/hooks/useLatest';
import Border from './border';
import GrabPoints from './grabPointsContainer';
import Labels from './labels';
import Rotation from './rotation';

type Point = {
    x: number;
    y: number;
};

interface NPointWarpProps {
    container: HTMLElement;
    documentId: string;
    transformId: string;
    points: Point[];
    selectedPoint?: number;
    hidden?: boolean;
    selected: boolean;
    scale: number;
    cursorChange: (cursor: string) => void;
    offset: Point;
    onDragEnd: (params: { id: string; transformId: string; points: Point[] }) => void;
    rotationAngle?: number;
    setRotationAngle: (params: { angle: number; id: string }) => void;
    selectLayer: (documentId: string) => void;
}

const NPointWarp: React.FC<NPointWarpProps> = (props) => {
    const {
        scale,
        cursorChange,
        documentId,
        transformId,
        selectedPoint,
        offset,
        selected,
        selectLayer,
        hidden,
        container,
        rotationAngle,
        onDragEnd: onDragEndProp,
        setRotationAngle: setRotationAngleProp,
    } = props;

    // `dragging`/`points` are kept as one state object, updated via a single `setState` call per
    // transition, rather than two independent `useState` hooks -- this tree is mounted inside
    // react-konva's <Stage>, under React's legacy root mode, so updates triggered by Konva's own
    // event dispatch (as `onDragMove` below is) don't get React 18's automatic batching. Two
    // separate setters here means `setPoints(newPoints)` commits its own render first, with
    // `dragging` still `false`, which triggers the "sync back to props while not dragging" check
    // below and reverts `points` to `props.points` before `setDragging(true)` commits -- the net
    // effect is the warp doesn't visibly move on the very first drag-move event of every gesture.
    // Combining into one atomic update avoids the intermediate `!dragging`-but-already-moved
    // render entirely. (See rulers.tsx for the sibling bug where the same split caused an outright
    // crash instead of a dropped frame.)
    const [dragState, setDragState] = useState<{ dragging: boolean; points: Point[] }>({
        dragging: false,
        points: props.points,
    });
    const { dragging, points } = dragState;

    if (!dragging && points !== props.points) {
        setDragState({ dragging: false, points: props.points });
    }

    // Border/Rotation/GrabPoints' `useDragGesture` calls mirror the latest `onDragEnd` they're given
    // every render, but that closure's own body still needs a *live* `points` at call time --
    // `useLatest` covers that (see its doc comment).
    const pointsRef = useLatest(points);

    const setRotationAngle = (angle: number) => setRotationAngleProp({ angle, id: documentId });

    // We have to create our own drag move and drag end events since when react re-renders, it forgets that it was being dragged.
    const onDragMove = (newPoints: Point[]) => {
        pointsRef.current = newPoints;
        setDragState({ dragging: true, points: newPoints });
    };

    const onDragEnd = () => {
        onDragEndProp({ id: documentId, transformId, points: pointsRef.current });
        setDragState((prev) => ({ ...prev, dragging: false }));
    };

    if (!points || !points.length || hidden) {
        return null;
    }

    const scaledPoints = points.map((point) => ({ x: point.x * scale + offset.x, y: point.y * scale + offset.y }));

    let scaledAndExpandedPoints = scaledPoints;
    if (scaledPoints.length === 2) {
        scaledAndExpandedPoints = [
            scaledPoints[0],
            { x: scaledPoints[1].x, y: scaledPoints[0].y },
            scaledPoints[1],
            { x: scaledPoints[0].x, y: scaledPoints[1].y },
        ];
    }

    return (
        <Group>
            <Border
                selected={selected}
                points={points}
                scaledPoints={scaledAndExpandedPoints}
                scale={scale}
                onDragMove={onDragMove}
                onDragEnd={onDragEnd}
                selectLayer={() => selectLayer(documentId)}
            />
            {selected && (
                <Group>
                    {points.length > 2 && (
                        <Rotation
                            container={container}
                            scaledPoints={scaledAndExpandedPoints}
                            points={points}
                            scale={scale}
                            onDragMove={onDragMove}
                            onDragEnd={onDragEnd}
                            cursorChange={cursorChange}
                            offset={offset}
                            rotationAngle={rotationAngle!}
                            setRotationAngle={setRotationAngle}
                        />
                    )}
                    <GrabPoints
                        points={points}
                        scaledPoints={scaledAndExpandedPoints}
                        scale={scale}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        cursorChange={cursorChange}
                        documentId={documentId}
                        selectedPoint={selectedPoint}
                        rotationAngle={rotationAngle!}
                    />
                    <Labels points={scaledPoints} selectedPoint={selectedPoint} />
                </Group>
            )}
        </Group>
    );
};

export default NPointWarp;
